/**
 * 电商套图应用 · API 客户端
 */
import { platformFetch, streamJsonEvents } from '../../platform/http';

export interface SuiteTemplateSummary {
  id: string;
  name: string;
  keywords: string[];
  variants: Array<{ key: string; description: string }>;
  categoryTips: string[];
  supportsImageReference: boolean;
}

export interface SuiteProjectListItem {
  id: string;
  title: string;
  productName: string | null;
  productCategory: string | null;
  targetPlatform: string | null;
  status: string; // draft/planned/generating/partial/completed
  productImageList: string[];
  shotCount: number;
  completedCount: number;
  coverImages: (string | null)[];
  createdAt: string;
  updatedAt: string;
}

export type SuiteProtectionMode = 'PIXEL_PROTECTED' | 'CREATIVE';

export type SuiteReviewStatus = 'pending' | 'approved' | 'needs_redo';

export interface SuiteShot {
  id: string;
  projectId: string;
  templateId: string | null;
  templateVariant: string | null;
  displayName: string;
  prompt: string | null;
  protectionMode: SuiteProtectionMode;
  size: string;
  quality: string;
  model: string | null;
  status: 'idle' | 'generating' | 'success' | 'failed';
  reviewStatus: SuiteReviewStatus;
  outputImage: string | null;
  imageJobId: string | null;
  errorMessage: string | null;
  generation: number;
  factClaims: string | null;
  riskFlags: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface SuitePlanInfo {
  id: string;
  mode: string;
  llmModelId: string | null;
  createdAt: string;
}

export interface SuiteProjectDetail {
  id: string;
  title: string;
  productCategory: string | null;
  productName: string | null;
  productDescription: string | null;
  verifiedFacts: string | null;
  prohibitedClaims: string | null;
  targetPlatform: string | null;
  targetMarket: string | null;
  copyLanguage: string;
  defaultProtection: SuiteProtectionMode;
  productImageList: string[];
  referenceImageList: string[];
  status: string;
  styleLock: string | null;
  createdAt: string;
  updatedAt: string;
  shots: SuiteShot[];
  plan: SuitePlanInfo | null;
}

export interface SuiteCreditsPreview {
  credits: number;
  shotCount: number;
  mode: 'img2img' | 'text2img';
  currentCredits: number;
  enough: boolean;
  shots: Array<{ shotId: string; displayName: string; credits: number }>;
}

export interface SaveProjectPayload {
  title?: string;
  productName?: string | null;
  productCategory?: string | null;
  productDescription?: string | null;
  verifiedFacts?: string | null;
  prohibitedClaims?: string | null;
  targetPlatform?: string | null;
  targetMarket?: string | null;
  copyLanguage?: string | null;
  defaultProtection?: SuiteProtectionMode | null;
}

export interface SuiteShotOverrides {
  model?: string;
  size?: string;
  quality?: string;
}

async function requestJson<T>(url: string, init?: RequestInit, fallbackError = '请求失败'): Promise<T> {
  const response = await platformFetch(url, init);
  if (!response.ok) {
    let message = fallbackError;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch { /* keep fallback */ }
    throw new Error(message);
  }
  return response.json();
}

export const ecommerceSuiteAPI = {
  async getTemplates(): Promise<{ data: SuiteTemplateSummary[] }> {
    return requestJson('/ecommerce-suite/templates', undefined, '获取模板目录失败');
  },

  async getProjects(): Promise<{ data: SuiteProjectListItem[] }> {
    return requestJson('/ecommerce-suite/projects', undefined, '获取项目列表失败');
  },

  async getProject(id: string): Promise<{ data: SuiteProjectDetail }> {
    return requestJson(`/ecommerce-suite/projects/${id}`, undefined, '获取项目详情失败');
  },

  async createProject(data: SaveProjectPayload): Promise<{ data: SuiteProjectDetail }> {
    return requestJson('/ecommerce-suite/projects', { method: 'POST', body: JSON.stringify(data) }, '创建项目失败');
  },

  async updateProject(id: string, data: SaveProjectPayload): Promise<{ data: SuiteProjectDetail }> {
    return requestJson(`/ecommerce-suite/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, '更新项目失败');
  },

  async deleteProject(id: string): Promise<{ message: string }> {
    return requestJson(`/ecommerce-suite/projects/${id}`, { method: 'DELETE' }, '删除项目失败');
  },

  async updateAssets(id: string, data: { productImages?: string[]; referenceImages?: string[] }): Promise<{ data: SuiteProjectDetail }> {
    return requestJson(`/ecommerce-suite/projects/${id}/assets`, { method: 'POST', body: JSON.stringify(data) }, '更新素材失败');
  },

  async assistDescription(id: string, data: { title?: string; productName?: string; productCategory?: string; rawText: string }): Promise<{ data: { description: string } }> {
    return requestJson(`/ecommerce-suite/projects/${id}/assist-description`, { method: 'POST', body: JSON.stringify(data) }, 'AI 帮写失败');
  },

  /**
   * AI 规划（SSE）。onContent 接收原始流式文本（供过程展示），结束时 onResult 回传完整方案。
   */
  async planStream(
    id: string,
    body: { mode: 'ai' | 'manual'; requestedTemplateIds?: string[]; targetImageCount?: number; userInstruction?: string },
    handlers: {
      onContent?: (text: string) => void;
      onResult?: (result: { styleLock: string; shots: SuiteShot[] }) => void;
      onError?: (message: string) => void;
    }
  ): Promise<void> {
    const response = await platformFetch(`/ecommerce-suite/projects/${id}/plan/stream`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let message = 'AI 规划失败';
      try {
        const body = await response.json();
        if (body?.error) message = body.error;
      } catch { /* keep fallback */ }
      handlers.onError?.(message);
      return;
    }

    await streamJsonEvents(response, {
      onEvent: (event) => {
        if (typeof event.content === 'string') handlers.onContent?.(event.content);
        if (event.error && typeof event.error === 'string') handlers.onError?.(event.error);
        if (event.result && typeof event.result === 'object') {
          handlers.onResult?.(event.result as { styleLock: string; shots: SuiteShot[] });
        }
      },
    });
  },

  async updateShot(id: string, data: {
    displayName?: string;
    prompt?: string | null;
    protectionMode?: SuiteProtectionMode;
    size?: string;
    quality?: string;
  }): Promise<{ data: SuiteShot }> {
    return requestJson(`/ecommerce-suite/shots/${id}`, { method: 'PATCH', body: JSON.stringify(data) }, '更新分镜失败');
  },

  async createShot(projectId: string, data: {
    templateId?: string;
    displayName: string;
    prompt?: string;
    protectionMode?: SuiteProtectionMode;
  }): Promise<{ data: SuiteShot }> {
    return requestJson(`/ecommerce-suite/projects/${projectId}/shots`, { method: 'POST', body: JSON.stringify(data) }, '创建分镜失败');
  },

  async deleteShot(id: string): Promise<{ message: string }> {
    return requestJson(`/ecommerce-suite/shots/${id}`, { method: 'DELETE' }, '删除分镜失败');
  },

  async reorderShots(projectId: string, shotIds: string[]): Promise<{ message: string }> {
    return requestJson(`/ecommerce-suite/projects/${projectId}/shots-order`, { method: 'PATCH', body: JSON.stringify({ shotIds }) }, '排序失败');
  },

  async generateShot(id: string, overrides?: SuiteShotOverrides): Promise<{ message: string; shotId: string; jobId: string }> {
    return requestJson(`/ecommerce-suite/shots/${id}/generate`, { method: 'POST', body: JSON.stringify(overrides || {}) }, '生成失败');
  },

  async rerollShot(id: string): Promise<{ message: string; shotId: string; jobId: string }> {
    return requestJson(`/ecommerce-suite/shots/${id}/reroll`, { method: 'POST' }, '重新生成失败');
  },

  /** 蒙版局部编辑（inpaint）：maskImage 为画布涂抹产生的 dataURL */
  async editShot(id: string, data: { instruction: string; maskImage: string }): Promise<{ message: string; shotId: string; jobId: string }> {
    return requestJson(`/ecommerce-suite/shots/${id}/edit`, { method: 'POST', body: JSON.stringify(data) }, '局部编辑失败');
  },

  /** 审核标记：approved / needs_redo / pending */
  async reviewShot(id: string, reviewStatus: SuiteReviewStatus): Promise<{ data: SuiteShot }> {
    return requestJson(`/ecommerce-suite/shots/${id}/review`, { method: 'PATCH', body: JSON.stringify({ reviewStatus }) }, '审核标记失败');
  },

  /** 按风格锁重生成全组（除生成中外全部重 roll，审核状态重置） */
  async regenerateAll(projectId: string): Promise<{ message: string; results: Array<{ shotId: string; jobId?: string; error?: string }> }> {
    return requestJson(`/ecommerce-suite/projects/${projectId}/regenerate-all`, { method: 'POST' }, '全组重生成失败');
  },

  async generateAll(projectId: string, shotIds?: string[]): Promise<{ message: string; results: Array<{ shotId: string; jobId?: string; error?: string }> }> {
    return requestJson(`/ecommerce-suite/projects/${projectId}/generate-all`, { method: 'POST', body: JSON.stringify({ shotIds }) }, '批量生成失败');
  },

  async getCreditPreview(projectId: string): Promise<{ data: SuiteCreditsPreview }> {
    return requestJson(`/ecommerce-suite/projects/${projectId}/credit-preview`, undefined, '获取积分预览失败');
  },
};
